/**
 * How the app looks and sounds for one child
 * ([D-132](../../../docs/DECISIONS.md#d-132), [D-133](../../../docs/DECISIONS.md#d-133)).
 *
 * Per child rather than per tablet: two siblings share an iPad and only one of
 * them may want bigger text, and a switch that flips for the whole family every
 * time one of them signs in is a switch nobody trusts. Kept on the device,
 * because these are ways of *showing* the app, not facts about a child's
 * learning, and nothing on the server needs them.
 *
 * Every default is the app as it is without the settings screen, so a child who
 * never opens it, or a device whose storage is blocked, loses nothing.
 *
 * What the display switches change lives in
 * [`base.css`](../../../packages/tokens/src/base.css) under `data-reading`,
 * `data-text` and `data-motion` on the root; this only sets the attributes.
 */

export interface Prefs {
  /** Wider spacing, a clear face and a cream page. */
  readonly easierReading: boolean;
  /** Everything a size up, over whatever the device already asks for. */
  readonly biggerText: boolean;
  /** Reduced motion, whether or not the device asks for it. */
  readonly lessMovement: boolean;
  /** Sneakers says his lines out loud. Off, he still shows them, and a tap still plays one. */
  readonly sneakersTalks: boolean;
  /** Speaking exercises listen. Off, they arrive as "say it out loud yourself". */
  readonly useMic: boolean;
}

export const DEFAULT_PREFS: Prefs = {
  easierReading: false,
  biggerText: false,
  lessMovement: false,
  sneakersTalks: true,
  useMic: true,
};

const key = (learnerId: string) => `capytalk.prefs.${learnerId}`;

let active: Prefs = DEFAULT_PREFS;

/** The signed-in child's preferences, for code that is not handed them. */
export const activePrefs = (): Prefs => active;

/**
 * This child's preferences.
 *
 * Unknown keys are ignored and anything not a boolean falls back to its
 * default: storage is only ever as trustworthy as the last version that wrote
 * it.
 */
export function loadPrefs(learnerId: string): Prefs {
  let stored: unknown;
  try {
    stored = JSON.parse(globalThis.localStorage?.getItem(key(learnerId)) ?? 'null');
  } catch {
    stored = null;
  }
  if (typeof stored !== 'object' || stored === null) return DEFAULT_PREFS;
  const record = stored as Record<string, unknown>;
  const pick = (name: keyof Prefs): boolean => {
    const value = record[name];
    return typeof value === 'boolean' ? value : DEFAULT_PREFS[name];
  };
  return {
    easierReading: pick('easierReading'),
    biggerText: pick('biggerText'),
    lessMovement: pick('lessMovement'),
    sneakersTalks: pick('sneakersTalks'),
    useMic: pick('useMic'),
  };
}

/** Remember a change, and show it straight away. */
export function savePrefs(learnerId: string, prefs: Prefs): void {
  try {
    globalThis.localStorage?.setItem(key(learnerId), JSON.stringify(prefs));
  } catch {
    // Not remembered; it still applies for this visit.
  }
  applyPrefs(prefs);
}

const flag = (root: HTMLElement, name: string, value: string, on: boolean) => {
  if (on) root.dataset[name] = value;
  else delete root.dataset[name];
};

/** Put the whole page into these preferences. Null, on sign-out, puts it back. */
export function applyPrefs(prefs: Prefs | null): void {
  active = prefs ?? DEFAULT_PREFS;
  const root = globalThis.document?.documentElement;
  if (root === undefined) return;
  flag(root, 'reading', 'easier', active.easierReading);
  flag(root, 'text', 'bigger', active.biggerText);
  flag(root, 'motion', 'less', active.lessMovement);
}

/**
 * Whether anything should hold still: the device asked, or the child did.
 *
 * For the few things drawn by script rather than by CSS, which the stylesheet
 * rule cannot reach.
 */
export const holdStill = (): boolean =>
  active.lessMovement ||
  (typeof globalThis.matchMedia === 'function' &&
    globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches);
